/**
 * Alerts Dashboard Component
 *
 * Displays system alerts with filtering and summary cards
 * Admin-facing dashboard for monitoring system health
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AlertsDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AlertsDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    severity: 'all',
    days: 7,
    alert_type: 'all'
  });

  useEffect(() => {
    fetchAlerts();
    fetchSummary();
  }, [filters]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const params = {
        days: filters.days,
        limit: 50
      };

      if (filters.severity !== 'all') {
        params.severity = filters.severity;
      }

      if (filters.alert_type !== 'all') {
        params.alert_type = filters.alert_type;
      }

      const response = await axios.get(`${API_BASE_URL}/api/alerts/list`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      setError('Failed to load alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/alerts/summary`, {
        params: { days: filters.days },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch alert summary:', err);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/alerts/${alertId}/acknowledge`,
        { acknowledged_by: null }, // Will use current user's email
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Refresh alerts
      fetchAlerts();
      fetchSummary();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
      alert('Failed to acknowledge alert. Please try again.');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return '#dc3545';
      case 'high':
        return '#fd7e14';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="alerts-dashboard">
      <div className="dashboard-header">
        <h1>System Alerts</h1>
        <button className="refresh-button" onClick={() => { fetchAlerts(); fetchSummary(); }}>
          🔄 Refresh
        </button>
      </div>

      {/* Alert Summary Cards */}
      <div className="alert-summary">
        <div className="summary-card critical">
          <div className="card-header">
            <h3>Critical</h3>
            <span className="severity-icon">🚨</span>
          </div>
          <div className="count">{summary.critical}</div>
        </div>

        <div className="summary-card high">
          <div className="card-header">
            <h3>High</h3>
            <span className="severity-icon">⚠️</span>
          </div>
          <div className="count">{summary.high}</div>
        </div>

        <div className="summary-card medium">
          <div className="card-header">
            <h3>Medium</h3>
            <span className="severity-icon">ℹ️</span>
          </div>
          <div className="count">{summary.medium}</div>
        </div>

        <div className="summary-card low">
          <div className="card-header">
            <h3>Low</h3>
            <span className="severity-icon">📝</span>
          </div>
          <div className="count">{summary.low}</div>
        </div>

        <div className="summary-card total">
          <div className="card-header">
            <h3>Total</h3>
            <span className="severity-icon">📊</span>
          </div>
          <div className="count">{summary.total}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="severity-filter">Severity:</label>
          <select
            id="severity-filter"
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="days-filter">Time Period:</label>
          <select
            id="days-filter"
            value={filters.days}
            onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="type-filter">Alert Type:</label>
          <select
            id="type-filter"
            value={filters.alert_type}
            onChange={(e) => setFilters({ ...filters, alert_type: e.target.value })}
          >
            <option value="all">All Types</option>
            <option value="high_safety_events">High Safety Events</option>
            <option value="child_inactive">Child Inactive</option>
            <option value="parent_inactive">Parent Inactive</option>
            <option value="high_churn_risk">High Churn Risk</option>
            <option value="unusual_token_spike">Unusual Token Spike</option>
            <option value="monthly_burn_exceeds_budget">Budget Exceeded</option>
            <option value="daily_cost_spike">Daily Cost Spike</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && <div className="loading">Loading alerts...</div>}

      {/* Error State */}
      {error && <div className="error-message">{error}</div>}

      {/* Alerts List */}
      {!loading && !error && (
        <div className="alerts-list">
          {alerts.length === 0 ? (
            <div className="no-alerts">
              <p>✅ No alerts found for the selected filters.</p>
              <p className="subtext">This is good news! Your system is running smoothly.</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className={`alert-item ${alert.acknowledged ? 'acknowledged' : ''}`}
                style={{ borderLeftColor: getSeverityColor(alert.severity) }}
              >
                <div className="alert-header">
                  <span
                    className={`severity-badge ${alert.severity}`}
                    style={{ backgroundColor: getSeverityColor(alert.severity) }}
                  >
                    {alert.severity.toUpperCase()}
                  </span>
                  <span className="alert-type">{alert.alert_type.replace(/_/g, ' ')}</span>
                  <span className="alert-time">{formatTimestamp(alert.triggered_at)}</span>
                </div>

                <div className="alert-details">
                  {Object.entries(alert.data).map(([key, value]) => (
                    <div key={key} className="detail-item">
                      <span className="detail-key">{key.replace(/_/g, ' ')}:</span>
                      <span className="detail-value">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="alert-actions">
                  {alert.acknowledged ? (
                    <span className="acknowledged-badge">
                      ✓ Acknowledged by {alert.acknowledged_by} on{' '}
                      {formatTimestamp(alert.acknowledged_at)}
                    </span>
                  ) : (
                    <button
                      className="acknowledge-button"
                      onClick={() => acknowledgeAlert(alert.id)}
                    >
                      Mark as Acknowledged
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AlertsDashboard;
